import React, { useEffect, useState } from "react";
import { Dialog } from "../../components/ui/Dialog";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";
import Select from "react-select";
import { showToast } from "../../utils/toast";
import {
    getPartnersList,
    createMerchant,
    Merchant,
    createMerchantsBulk,
} from "../../services/merchant";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (merchant: Merchant) => void;
    formData: FormData;
    setFormData: React.Dispatch<React.SetStateAction<FormData>>;
    user?: any;
}

export interface FormData {
    name: string;
    kycShop: {
        address: string;
        city: string;
        state: string;
        pin: string;
    }
    mobileNo: string;
    email: string;
    afpId: string;
    kyc: string;
    bulkFile?: File;
    bulkAfpId?: string;
    bulkKyc?: string;
}

const CreateMerchantDialog: React.FC<Props> = ({
    isOpen,
    onClose,
    onCreate,
    formData,
    setFormData,
    user,
}) => {
    const [availableGroups, setAvailableGroups] = useState<string[]>([]);
    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [skippedFile, setSkippedFile] = useState<string | null>(null);

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const partners = await getPartnersList();
                setAvailableGroups(partners);
            } catch (err) {
                console.error("Error fetching partners", err);
            }
        };

        fetchGroups();
    }, [user]);

    // Prevent body scroll when Select is open
    useEffect(() => {
        if (isSelectOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isSelectOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // If field belongs to kycShop
        if (["address", "city", "state", "pincode", "pin"].includes(name)) {
            setFormData({
                ...formData,
                kycShop: {
                    ...formData.kycShop,
                    [name === "pincode" ? "pin" : name]: value, // map pincode → pin
                },
            });
            return;
        }

        // Normal fields
        setFormData({
            ...formData,
            [name]: value,
        });
    };


    const resetForm = () => {
        setFormData({
            name: "",
            kycShop: {
                address: "",
                city: "",
                state: "",
                pin: "",
            },
            mobileNo: "",
            email: "",
            afpId: "",
            kyc: "",
            bulkFile: undefined,
            bulkAfpId: "",
            bulkKyc: "",
        });
        setSkippedFile(null);
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.mobileNo || !formData.afpId) {
            showToast.error("Please fill all required fields");
            return;
        }

        setLoading(true);
        try {
            const createdMerchant = await createMerchant(formData);

            if (createdMerchant) {
                showToast.success(
                    `Merchant created successfully. ID: ${createdMerchant.merchantId}`
                );
                onCreate(createdMerchant);
                resetForm();
                onClose();
            } else {
                showToast.error("Failed to create merchant");
            }
        } catch (error: any) {
            const errMsg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "";

            if (errMsg.includes("E11000") && errMsg.includes("mobileNo")) {
                showToast.error("A merchant with this mobile number already exists");
            } else {
                showToast.error("Failed to create merchant");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBulkSubmit = async () => {
        if (!formData.bulkFile || !formData.bulkAfpId || !formData.bulkKyc) {
            showToast.error('Please select file, partner name, and KYC mode');
            return;
        }

        setBulkLoading(true);

        try {
            const result = await createMerchantsBulk(
                formData.bulkFile,
                formData.bulkAfpId,
                formData.bulkKyc as 'byadmin' | 'bymerchant'
            );

            if (result) {
                if (result.created > 0) {
                    showToast.success(`${result.created} merchants created successfully`);
                    resetForm();
                    onClose();
                } else {
                    showToast.info('No merchants were created. Please check your file.');
                }
            }
            // Skipped Excel already handled inside createMerchantsBulk
        } catch (err) {
            console.error('Bulk upload failed', err);
            showToast.error('Bulk merchant upload failed');
        } finally {
            setBulkLoading(false);
        }
    };


    return (
        <Dialog
            isOpen={isOpen}
            onClose={() => {
                resetForm();
                onClose();
            }}
            title="Create Merchant"
            size="xl"
            footer={
                <div className="flex justify-between items-center pt-4">
                    {/* Left Button */}
                    <div className="w-1/2 pr-3 flex justify-start">
                        <Button onClick={handleSubmit} disabled={loading || !formData.name || !formData.mobileNo || !formData.afpId}>
                            {loading ? "Creating..." : "Create Single Merchant"}
                        </Button>
                    </div>



                    {/* Right Button */}
                    <div className="w-1/2 pl-3 flex justify-end">
                        <Button
                            onClick={handleBulkSubmit}
                            disabled={bulkLoading || !formData.bulkFile || !formData.bulkAfpId || !formData.bulkKyc}
                        >
                            {bulkLoading ? "Uploading..." : "Create in Bulk"}
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="flex space-x-6">
                {/* LEFT: Single Merchant Form */}
                <div className="w-1/2 space-y-4 overflow-visible">
                    <h3 className="text-lg font-semibold text-gray-700">
                        Create Single Merchant
                    </h3>

                    <div className="space-y-1 z-50">
                        <label className="text-sm font-medium text-gray-700">
                            KYC Method <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={
                                formData.kyc
                                    ? {
                                        value: formData.kyc,
                                        label:
                                            formData.kyc === "byadmin" ? "By Admin" : "By Merchant",
                                    }
                                    : null
                            }
                            onChange={(option) =>
                                setFormData({ ...formData, kyc: option?.value || "bymerchant" })
                            }
                            options={[
                                { value: "bymerchant", label: "By Merchant" },
                                { value: "byadmin", label: "By Admin" },
                            ]}
                            placeholder="Select KYC Method"
                            classNamePrefix="react-select"
                            menuPortalTarget={
                                typeof window !== "undefined" ? document.body : null
                            }
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                        />
                    </div>
                    {formData.kyc === "byadmin" && (
                        <>
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">
                                    Address <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    name="address"
                                    placeholder="Merchant Address"
                                    value={formData.kycShop.address}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">
                                    City <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    name="city"
                                    placeholder="City"
                                    value={formData.kycShop.city}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">
                                    State <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    name="state"
                                    placeholder="State"
                                    value={formData.kycShop.state}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">
                                    Pincode <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    name="pincode"
                                    placeholder="Pincode"
                                    value={formData.kycShop.pin}
                                    onChange={handleChange}
                                />
                            </div>
                        </>
                    )}


                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            name="name"
                            placeholder="Merchant Name"
                            value={formData.name}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">
                            Mobile No <span className="text-red-500">*</span>
                        </label>
                        <Input
                            name="mobileNo"
                            placeholder="Merchant Mobile No"
                            value={formData.mobileNo}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">Email</label>
                        <Input
                            name="email"
                            placeholder="Merchant Email"
                            value={formData.email}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="space-y-1 z-50">
                        <label className="text-sm font-medium text-gray-700">
                            AFP ID (Partner Name) <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={
                                formData.afpId ? { value: formData.afpId, label: formData.afpId } : null
                            }
                            onChange={(option) =>
                                setFormData({ ...formData, afpId: option?.value || "" })
                            }
                            options={availableGroups.map((g) => ({ value: g, label: g }))}
                            isClearable
                            placeholder="Search or select partner..."
                            classNamePrefix="react-select"
                            menuPortalTarget={
                                typeof window !== "undefined" ? document.body : null
                            }
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                menuList: (base) => ({
                                    ...base,
                                    maxHeight: "150px",
                                    overflowY: "auto",
                                    paddingRight: 0,
                                    scrollbarWidth: "thin",
                                }),
                            }}
                        />
                    </div>


                </div>

                {/* Divider */}
                <div className="w-px bg-gradient-to-b from-gray-300 to-gray-100" />

                {/* RIGHT: Bulk Upload */}
                <div className="w-1/2 space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700">
                        Bulk Merchants Upload
                    </h3>
                    <div className="space-y-1 z-50">
                        <label className="text-sm font-medium text-gray-700">
                            KYC Method <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={
                                formData.bulkKyc
                                    ? {
                                        value: formData.bulkKyc,
                                        label:
                                            formData.bulkKyc === "byadmin"
                                                ? "By Admin"
                                                : "By Merchant",
                                    }
                                    : null
                            }
                            onChange={(option) =>
                                setFormData({ ...formData, bulkKyc: option?.value || "bymerchant" })
                            }
                            options={[
                                { value: "bymerchant", label: "By Merchant" },
                                { value: "byadmin", label: "By Admin" },
                            ]}
                            placeholder="Select KYC Method"
                            classNamePrefix="react-select"
                            menuPortalTarget={
                                typeof window !== "undefined" ? document.body : null
                            }
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">
                            Excel File <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    setFormData({ ...formData, bulkFile: file });
                                }
                            }}
                            className="block w-full border rounded px-3 py-2 text-sm text-gray-700 border-gray-300"
                        />
                    </div>

                    <div className="space-y-1 z-50">
                        <label className="text-sm font-medium text-gray-700">
                            AFP ID (Partner Name)
                            <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={
                                formData.bulkAfpId
                                    ? { value: formData.bulkAfpId, label: formData.bulkAfpId }
                                    : null
                            }
                            onChange={(option) =>
                                setFormData({ ...formData, bulkAfpId: option?.value || "" })
                            }
                            options={availableGroups.map((g) => ({ value: g, label: g }))}
                            isClearable
                            placeholder="Select Partner for Bulk Upload"
                            classNamePrefix="react-select"
                            menuPortalTarget={
                                typeof window !== "undefined" ? document.body : null
                            }
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                menuList: (base) => ({
                                    ...base,
                                    maxHeight: "150px",
                                    overflowY: "auto",
                                    paddingRight: 0,
                                    scrollbarWidth: "thin",
                                }),
                            }}
                        />
                    </div>

                    {/* <div className="space-y-1 z-50">
                        <label className="text-sm font-medium text-gray-700">
                            KYC Method <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={
                                formData.bulkKyc
                                    ? {
                                        value: formData.bulkKyc,
                                        label:
                                            formData.bulkKyc === "byadmin"
                                                ? "By Admin"
                                                : "By Merchant",
                                    }
                                    : null
                            }
                            onChange={(option) =>
                                setFormData({ ...formData, bulkKyc: option?.value || "bymerchant" })
                            }
                            options={[
                                { value: "bymerchant", label: "By Merchant" },
                                { value: "byadmin", label: "By Admin" },
                            ]}
                            placeholder="Select KYC Method"
                            classNamePrefix="react-select"
                            menuPortalTarget={
                                typeof window !== "undefined" ? document.body : null
                            }
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                        />
                    </div> */}
                </div>
            </div>
        </Dialog>
    );
};

export default CreateMerchantDialog;
